import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import React from 'react';
import { BlackTechStreetData, latest, pctChange, previous } from '../types';
import { getAnnualPaceStatus } from '../../statusUtils';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

export function EngagementsCard({ data }: { data: BlackTechStreetData }) {
  const theme = useMantineTheme();
  const series = data.engagements;
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);

  const totalThisQuarter =
    (cur?.labResources ?? 0) + (cur?.startups ?? 0) + (cur?.productsDeveloped ?? 0) + (cur?.productsCommercialized ?? 0) + (cur?.productsScaled ?? 0);
  const totalPrevQuarter = prev
    ? prev.labResources + prev.startups + prev.productsDeveloped + prev.productsCommercialized + prev.productsScaled
    : null;
  const qoqTotal = pctChange(totalThisQuarter, totalPrevQuarter);
  const qoqArrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqLabel = (n: number | null): string => (n === null ? '— QoQ' : `${qoqArrow(n)}${Math.abs(n).toFixed(2)}% QoQ`);

  const parseQuarter = (label: string): { year: number; quarter: number } | null => {
    const match = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (!match) return null;
    return { year: Number(match[2]), quarter: Number(match[1]) };
  };

  const currentMeta = parseQuarter(cur?.quarter ?? '');
  const totalYtd = currentMeta
    ? series
        .filter((point) => {
          const meta = parseQuarter(point.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce(
          (acc, p) =>
            acc + p.labResources + p.startups + p.productsDeveloped + p.productsCommercialized + p.productsScaled,
          0,
        )
    : 0;

  const yoyTotal = (() => {
    if (!currentMeta) return null;
    const prevYearTotal = series
      .filter((point) => {
        const meta = parseQuarter(point.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce(
        (acc, p) =>
          acc + p.labResources + p.startups + p.productsDeveloped + p.productsCommercialized + p.productsScaled,
        0,
      );
    return pctChange(totalYtd, prevYearTotal);
  })();
  const yoyLabel = (n: number | null): string => (n === null ? '— YoY' : `${qoqArrow(n)}${Math.abs(n).toFixed(2)}% YoY`);

  const totalAllTime = series.reduce(
    (acc, p) => acc + p.labResources + p.startups + p.productsDeveloped + p.productsCommercialized + p.productsScaled,
    0,
  );

  const status = getAnnualPaceStatus({ ytd: totalYtd, goalPerYear: 40, quarterLabel: cur?.quarter ?? 'Q1 1970', direction: 'min' });

  const roundedTopShape = (props: any, isTop: boolean) => {
    const { x, y, width, height, fill } = props;
    if (!height || height <= 0) {
      return <rect x={x} y={y} width={width} height={0} fill="transparent" />;
    }
    if (!isTop) {
      return <rect x={x} y={y} width={width} height={height} fill={fill} />;
    }
    const r = Math.min(6, width / 2, height);
    const path = `M ${x},${y + r} Q ${x},${y} ${x + r},${y} L ${x + width - r},${y} Q ${x + width},${y} ${x + width},${y + r} L ${x + width},${y + height} L ${x},${y + height} Z`;
    return <path d={path} fill={fill} />;
  };

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  return (
    <CardShell
      title="Microsoft Lab Engagements"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Total engagements this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {totalThisQuarter}
                </Text>
              ),
              sublabel: qoqLabel(qoqTotal),
            },
            {
              label: 'YTD',
              labelTooltip: 'Total engagements year-to-date. Annual goal: 40+ engagements per year.',
              value: (
                <Text size="lg" weight={800}>
                  {totalYtd}
                </Text>
              ),
              sublabel: (
                <Stack spacing={0}>
                  <Text size="xs" color="dimmed">
                    {yoyLabel(yoyTotal)}
                  </Text>
                  <Text size="xs" color="dimmed">
                    Goal: 40+ / Year
                  </Text>
                </Stack>
              ),
            },
            {
              label: 'All-Time',
              labelTooltip: 'Total engagements across all time (includes periods not shown).',
              value: (
                <Text size="lg" weight={800}>
                  {totalAllTime}
                </Text>
              ),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Quarterly Engagements by Type
        </Text>

        <Box sx={{ height: 180 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
              <XAxis
                dataKey="quarter"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={12}
              />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={40}
                tickMargin={8}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const p: any = payload[0]?.payload;
                  if (!p) return null;
                  const total =
                    p.labResources + p.startups + p.productsDeveloped + p.productsCommercialized + p.productsScaled;
                  const productsTotal = p.productsDeveloped + p.productsCommercialized + p.productsScaled;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontWeight: 900, fontSize: 14, lineHeight: 1.2, marginBottom: 6 }}>
                        {total} Engagements
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.9, lineHeight: 1.35 }}>
                        Lab Resources: {p.labResources}
                        <br />
                        Startups: {p.startups}
                        <br />
                        Products: {productsTotal}
                        <br />
                        <span style={{ opacity: 0.85 }}>
                          &nbsp;&nbsp;• Developed: {p.productsDeveloped}
                          <br />
                          &nbsp;&nbsp;• Commercialized: {p.productsCommercialized}
                          <br />
                          &nbsp;&nbsp;• Scaled: {p.productsScaled}
                        </span>
                      </div>
                    </div>
                  );
                }}
              />
              <Bar dataKey="labResources" name="Lab Resources" fill={theme.colors.blue[6]} radius={[6, 6, 0, 0]} isAnimationActive={false} />
              <Bar dataKey="startups" name="Startups" fill={theme.colors.violet[6]} radius={[6, 6, 0, 0]} isAnimationActive={false} />
              <Bar
                dataKey="productsDeveloped"
                name="Developed"
                stackId="products"
                fill="#7A8CA6"
                shape={(props: any) =>
                  roundedTopShape(
                    props,
                    props?.payload?.productsScaled === 0 &&
                      props?.payload?.productsCommercialized === 0 &&
                      (props?.payload?.productsDeveloped ?? 0) > 0,
                  )
                }
                isAnimationActive={false}
              />
              <Bar
                dataKey="productsCommercialized"
                name="Commercialized"
                stackId="products"
                fill={theme.colors.cyan[6]}
                shape={(props: any) =>
                  roundedTopShape(
                    props,
                    (props?.payload?.productsScaled ?? 0) === 0 &&
                      (props?.payload?.productsCommercialized ?? 0) > 0,
                  )
                }
                isAnimationActive={false}
              />
              <Bar
                dataKey="productsScaled"
                name="Scaled"
                stackId="products"
                fill="#C46A3D"
                shape={(props: any) => roundedTopShape(props, (props?.payload?.productsScaled ?? 0) > 0)}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px 12px',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: 12,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: theme.colors.blue[6],
                flex: '0 0 auto',
              }}
            />
            <Text size="xs" color="dimmed">
              Lab Resources
            </Text>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: theme.colors.violet[6],
                flex: '0 0 auto',
              }}
            />
            <Text size="xs" color="dimmed">
              Startups
            </Text>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: '#7A8CA6',
                flex: '0 0 auto',
              }}
            />
            <Text size="xs" color="dimmed">
              Products (Developed)
            </Text>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: theme.colors.cyan[6],
                flex: '0 0 auto',
              }}
            />
            <Text size="xs" color="dimmed">
              Products (Commercialized)
            </Text>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: '#C46A3D',
                flex: '0 0 auto',
              }}
            />
            <Text size="xs" color="dimmed">
              Products (Scaled)
            </Text>
          </Box>
        </Box>
      </Stack>
    </CardShell>
  );
}
